import { useState } from "react";
import { IssueReconcile } from "@/pages/admin/V3";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, PackagePlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { num } from "@/lib/format";
import { Field, PageHeader, Section } from "@/components/common/kit";
import { ProductSelect, SalesmanSelect, useProducts } from "@/components/common/Filters";
import RemoteTable from "@/components/common/RemoteTable";
import IssueSlip from "@/components/common/IssueSlip";

export default function StockIssue() {
  const { bizDate } = useAuth();
  const products = useProducts(true);
  const [sid, setSid] = useState("");
  const [date, setDate] = useState(bizDate);
  const [items, setItems] = useState([{ product_id: "", qty: "" }]);
  const [remarks, setRemarks] = useState("");
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  const [corr, setCorr] = useState(null);
  const [reason, setReason] = useState("");
  const saveCorr = async () => {
    try {
      await api.put(`/issues/${corr.id}/correct`, { reason, items: corr.items.filter((x) => x.product_id && Number(x.qty) > 0).map((x) => ({ product_id: x.product_id, qty: Number(x.qty) })) });
      toast.success("Issue corrected — warehouse and salesman stock updated");
      setCorr(null); setReason(""); setRk(rk + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const { data: stock } = useFetch(sid ? "/stock/current" : null, { salesman_id: sid }, rk);

  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const total = items.reduce((s, x) => s + (Number(x.qty) || 0), 0);

  const submit = async () => {
    const valid = items.filter((x) => x.product_id && Number(x.qty) > 0);
    if (!sid) return toast.error("Select a salesman.");
    if (!valid.length) return toast.error("Add at least one product with quantity.");
    setBusy(true);
    try {
      const r = await api.post("/issues", { salesman_id: sid, date, remarks, client_key: crypto.randomUUID(), items: valid.map((x) => ({ product_id: x.product_id, qty: Number(x.qty) })) });
      toast.success(`Stock issued · ${r.data.issue_no}`);
      setSlip(r.data);
      setItems([{ product_id: "", qty: "" }]);
      setRemarks("");
      setRk(rk + 1);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cols = [
    { key: "issue_no", label: "Issue ID" }, { key: "date", label: "Date", type: "date" }, { key: "salesman_name", label: "Salesman" },
    { key: "items", label: "Products", render: (r) => <span className="text-xs">{r.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</span> },
    { key: "total_packs", label: "Packs", type: "int" }, { key: "issued_by", label: "Issued by" }, { key: "created_at", label: "Time", type: "datetime" },
    { key: "slip", label: "", sortable: false, render: (r) => <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => setSlip(r)} data-testid={`view-slip-${r.issue_no}`}>Slip</Button><Button size="sm" variant="ghost" onClick={() => setCorr({ id: r.id, no: r.issue_no, items: r.items.map((i) => ({ product_id: i.product_id, qty: i.qty })) })} data-testid={`correct-${r.issue_no}`}>Correct</Button>{r.corrected && <span className="text-[10px] font-bold text-orange-600">CORRECTED</span>}</div> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Stock Issue" subtitle="Issue packs to a salesman. Each issue is recorded separately (morning or additional during the day)." />
      <div className="grid gap-6 lg:grid-cols-3">
        <Section title="New Issue" className="lg:col-span-2" testid="issue-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Salesman"><SalesmanSelect value={sid} onChange={setSid} activeOnly className="w-full" testid="issue-salesman-select" /></Field>
            <Field label="Date"><Input type="date" value={date} max={bizDate} onChange={(e) => setDate(e.target.value)} data-testid="issue-date" /></Field>
          </div>
          <div className="mt-4 space-y-2">
            {items.map((x, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1"><ProductSelect value={x.product_id} onChange={(v) => upd(i, "product_id", v)} products={products} testid={`issue-product-${i}`} /></div>
                <Input type="number" min={1} placeholder="Packs" className="h-10 w-28 text-right font-mono" value={x.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`issue-qty-${i}`} />
                <Button variant="ghost" size="icon" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`issue-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { product_id: "", qty: "" }])} data-testid="issue-add-row"><Plus className="mr-1 h-4 w-4" /> Add product</Button>
          </div>
          <div className="mt-4"><Field label="Remarks"><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="issue-remarks" /></Field></div>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <p className="font-mono text-sm">Total: <b data-testid="issue-total">{num(total)}</b> packs</p>
            <Button onClick={submit} disabled={busy} data-testid="issue-confirm-button"><PackagePlus className="mr-1.5 h-4 w-4" /> Confirm Issue</Button>
          </div>
        </Section>
        <Section title="Salesman current stock" testid="issue-current-stock">
          {!sid ? <p className="text-sm text-slate-500">Select a salesman.</p> : (
            <ul className="space-y-1.5 text-sm">
              {(stock?.items || []).map((s) => (
                <li key={s.product_id} className="flex justify-between"><span>{s.name}</span><span className="font-mono font-semibold">{num(s.qty)}</span></li>
              ))}
              <li className="flex justify-between border-t pt-1.5 font-bold"><span>Total</span><span className="font-mono">{num(stock?.total)}</span></li>
            </ul>
          )}
        </Section>
      </div>
      <Section title="Issue Register">
        <RemoteTable url="/issues" params={{ salesman_id: sid || undefined }} reloadKey={rk} columns={cols} testid="issues-table" />
      </Section>
      <IssueSlip issue={slip} onClose={() => setSlip(null)} />
      <IssueReconcile key={rk} />
      {corr && <Section title={`Correct ${corr.no}`} testid="issue-correction">
        <p className="mb-2 text-xs text-slate-500">Change product/quantity, remove or add items. Blocked if the issue date is in a closed day or stock was already sold — reopen the day or use an adjustment.</p>
        {corr.items.map((x, i) => <div key={i} className="mb-2 flex gap-2"><div className="flex-1"><ProductSelect value={x.product_id} onChange={(v) => setCorr({ ...corr, items: corr.items.map((y, j) => j === i ? { ...y, product_id: v } : y) })} products={products} testid={`corr-product-${i}`} /></div>
          <Input type="number" className="w-28" value={x.qty} onChange={(e) => setCorr({ ...corr, items: corr.items.map((y, j) => j === i ? { ...y, qty: e.target.value } : y) })} data-testid={`corr-qty-${i}`} />
          <Button variant="ghost" size="icon" onClick={() => setCorr({ ...corr, items: corr.items.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button></div>)}
        <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={() => setCorr({ ...corr, items: [...corr.items, { product_id: "", qty: "" }] })}>Add item</Button>
          <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="w-72" data-testid="corr-reason" />
          <Button size="sm" disabled={reason.length < 3} onClick={saveCorr} data-testid="corr-save">Save correction</Button><Button size="sm" variant="ghost" onClick={() => setCorr(null)}>Close</Button></div>
      </Section>}
    </div>
  );
}
